import { useEffect, useState, type ReactNode } from 'react';
import {
  api,
  ApiRequestError,
  useApi,
  type CredentialCheck,
  type CredentialKind,
  type CredentialsData,
  type CredentialView,
} from '../api';
import {
  Button,
  Card,
  CopyButton,
  EmptyState,
  ErrorBanner,
  Icon,
  Loading,
  formatDateTime,
  haceCuanto,
} from '../components/ui';
import { useAviso } from '../avisos';
import { ListaDeCorreos } from '../components/Correos';
import { useT, type Clave } from '../i18n';

/**
 * Credenciales de servicios externos.
 *
 * ── Por qué esta pantalla existe ────────────────────────────────────────────────
 * Antes, cambiar una clave era editar `.dev.vars` y reiniciar, y agregar una segunda cuenta
 * de Cloudflare era tocar el Makefile. Eso es operable por quien escribió el proyecto y por
 * nadie más.
 *
 * ── Lo que la hace útil, y no un formulario más ─────────────────────────────────
 * Guardar **comprueba en el acto** contra la API del proveedor y muestra qué permiso falta.
 * Sin eso, una clave mal puesta sólo falla cuando arranca una generación, a mitad de un
 * workflow y envuelta en reintentos.
 *
 * Ninguna comprobación ejecuta un modelo: preguntan por catálogos y metadatos. Verificar que
 * se puede generar no debería gastar la cuota que se intenta preservar.
 */

/**
 * Contraseña del panel.
 *
 * ── Por qué encabeza esta pantalla ──────────────────────────────────────────────
 * Todo lo que hay debajo son secretos: tokens de Cloudflare, claves de API, contraseñas de
 * cuentas. Lo único que los protege es esta contraseña. Ponerla al final, o peor —donde
 * estaba, detrás de `make hash-password` y un archivo—, hacía que casi nadie la cambiara.
 */
/**
 * Los dos pasos de Fanvue que son de la PERSONA y no del personaje.
 *
 * ── Por qué acá y no en la ficha de cada influencer ───────────────────────────
 * Porque se hacen una vez y valen para todas. Vivían como tareas dentro de cada influencer, y
 * ahí aparecían repetidos en las trece: se leían como «un KYC por cada una», que no es lo que
 * pide Fanvue —verifica a la persona que cobra, no al personaje— ni sería viable.
 *
 * Credenciales es la pantalla de «lo que vos tenés que tener listo», está acotada a cada
 * usuario y ya aloja la app de Fanvue. Es el lugar que corresponde.
 *
 * El estado sigue guardado en las tareas: es donde ya vivía, tiene auditoría y es lo que
 * consulta el resto del sistema. Marcarlo acá lo cierra en todas las influencers de una vez.
 */
function CuentaDeFanvue({ embedded = false }: { embedded?: boolean }) {
  const { t } = useT();
  const estado = useApi<{
    cuenta: boolean;
    kyc: boolean;
    managerEmail: string | null;
    /** Id de la credencial cuando hay una guardada. La contraseña nunca viaja en el estado. */
    managerPasswordId: string | null;
    correos: {
      id: string;
      remitente: string;
      asunto: string;
      cuerpo: string;
      recibidoEn: string;
    }[];
    dominioPropio: string | null;
    agencia: {
      conectada: boolean;
      puedePublicar: boolean;
      expiresAt: string | null;
      perfilesAsignados: number;
    } | null;
  }>('/api/fanvue/cuenta');
  const aviso = useAviso();
  const [busy, setBusy] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  /*
     y no cadena vacía: distingue «no escribí nada» de «lo borré».

    Es lo que permite que el botón de guardar esté apagado hasta que haya algo escrito, sin
    que un campo vacío al cargar la página cuente como intención de guardar.
  */
  const [clave, setClave] = useState<string | null>(null);

  // El resultado de conectar vuelve por la barra de direcciones: el callback es un redirect del
  // navegador. Se lee una vez y se limpia, para que recargar no repita el aviso.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resultado = params.get('fanvue');
    if (!resultado) return;

    if (resultado === 'ok') aviso.ok(t('cred.fanvueConectado'));
    else if (resultado === 'sin-permiso') {
      aviso.error(t('cred.sinPermisoPublicar'));
    } else if (resultado === 'error') {
      aviso.error(
        t('cred.fanvueRechazo', { motivo: params.get('motivo') ?? t('cred.sinDetalle') }),
      );
    }

    // Sólo al montar: el parámetro es de un solo uso y se limpia acá mismo.
    window.history.replaceState({}, '', window.location.pathname);
  }, [aviso]);

  if (!estado.data) return null;

  const guardarEmail = async () => {
    setBusy('email');
    try {
      await api.post('/api/fanvue/cuenta', { managerEmail: email ?? '' });
      aviso.ok(t('cred.correoManagerGuardado'));
      setEmail(null);
      estado.reload();
    } catch (caught) {
      if (!(caught instanceof ApiRequestError)) aviso.error(t('cred.noSePudoGuardar'));
    } finally {
      setBusy(null);
    }
  };

  const marcar = async (paso: 'CREATE_ACCOUNT' | 'COMPLETE_KYC') => {
    setBusy(paso);
    try {
      const r = await api.post<{ influencers: number }>('/api/fanvue/cuenta', { paso });
      aviso.ok(
        r.influencers > 0
          ? t(r.influencers === 1 ? 'cred.pasoCerradoUna' : 'cred.pasoCerradoVarias', {
              n: r.influencers,
            })
          : t('cred.listo'),
      );
      estado.reload();
    } catch (caught) {
      if (!(caught instanceof ApiRequestError)) aviso.error(t('cred.noSePudoGuardar'));
    } finally {
      setBusy(null);
    }
  };

  const pasos = [
    {
      clave: 'CREATE_ACCOUNT' as const,
      titulo: t('cred.tuCuentaDeCreador'),
      hecho: estado.data.cuenta,
      texto: t('cred.tuCuentaDeCreadorQue'),
    },
    {
      clave: 'COMPLETE_KYC' as const,
      titulo: t('cred.verificacion'),
      hecho: estado.data.kyc,
      texto: t('cred.verificacionQue'),
    },
  ];

  const content = (
    <>
      <p className="max-w-2xl text-sm text-[var(--color-ink-muted)]">
        {t('cred.dosPasosAntes')}
        <strong>{t('cred.unaSolaVez')}</strong>
        {t('cred.dosPasosDespues')}
      </p>

      <ul className="mt-4 space-y-3">
        {pasos.map((paso) => (
          <li
            key={paso.clave}
            className="rounded-xl border border-[var(--color-border-subtle)] p-3"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-medium">
                {paso.hecho ? '✓ ' : ''}
                {paso.titulo}
              </span>
              {paso.hecho ? (
                <span className="text-xs text-[var(--color-ok)]">{t('cred.hecho')}</span>
              ) : (
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={() => void marcar(paso.clave)}
                  className="rounded-lg border border-[var(--color-border-subtle)] px-2.5 py-1 text-xs hover:border-[var(--color-accent)]"
                >
                  {t(busy === paso.clave ? 'comun.guardando' : 'cred.yaLoHice')}
                </button>
              )}
            </div>
            {!paso.hecho && (
              <p className="mt-1.5 text-sm text-[var(--color-ink-muted)]">{paso.texto}</p>
            )}
          </li>
        ))}
      </ul>

      {/*
        La conexión de agencia: una sola para todas las influencers.

        Va acá y no en la ficha de cada una porque es exactamente eso — una autorización de
        manager que publica en nombre de todas. Conectar por influencer serían trece
        autorizaciones, trece tokens de una hora que renovar, y trece oportunidades de
        autorizar con la sesión equivocada.
      */}
      <div className="mt-5 border-t border-[var(--color-border-subtle)] pt-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-medium">
              {estado.data.agencia?.conectada ? '✓ ' : ''}
              {t('cred.conexionDeAgencia')}
            </p>
            <p className="mt-0.5 max-w-2xl text-sm text-[var(--color-ink-muted)]">
              {estado.data.agencia?.conectada
                ? estado.data.agencia.puedePublicar
                  ? t('cred.agenciaConectada', {
                      n: estado.data.agencia.perfilesAsignados,
                    })
                  : t('cred.agenciaSinPermiso')
                : t('cred.agenciaSinConectar')}
            </p>
          </div>
          <a
            href="/api/integrations/fanvue/agencia/connect"
            className="shrink-0 rounded-lg border border-[var(--color-border-subtle)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)]"
          >
            {t(estado.data.agencia?.conectada ? 'cred.volverAConectar' : 'cred.conectarFanvue')}
          </a>
        </div>

        {/*
          El armado de la agencia, paso a paso.

          ── Por qué cada paso dice con qué cuenta ─────────────────────────────
          Porque todo el trámite ocurre entre TRES cuentas de Fanvue distintas —la principal, la
          de manager y la de cada influencer— y el error que se comete es siempre el mismo:
          estar en una y hacer lo de otra.

          Ya pasó dos veces acá: pegar el correo de la influencer donde va el del manager, y
          autorizar la app con la cuenta principal en vez de la de agencia. Ninguno de los dos
          falla en el momento: dejan el sistema atado a la cuenta equivocada, y en Fanvue eso no
          se deshace porque el correo de una cuenta no se puede cambiar nunca.

          Por eso el aviso de las tres cuentas va arriba y visible, y cada paso lleva su etiqueta
          de sesión.
        */}
        {!estado.data.agencia?.conectada && (
          <div className="mt-4 max-w-2xl">
            <div className="rounded-xl border border-[var(--color-warn)]/40 bg-[var(--color-warn)]/10 p-3 text-sm">
              <p className="font-medium">{t('cred.tresCuentas')}</p>
              <ul className="mt-1.5 space-y-1 text-[var(--color-ink-muted)]">
                <li>
                  <strong>{t('cred.cuentaPrincipal')}</strong>
                  {t('cred.cuentaPrincipalQue')}
                </li>
                <li>
                  <strong>{t('cred.manager')}</strong>
                  {t('cred.managerQue')}
                </li>
                <li>
                  <strong>{t('cred.deCadaInfluencer')}</strong>
                  {t('cred.deCadaInfluencerQue')}
                </li>
              </ul>
              <p className="mt-2 text-xs text-[var(--color-ink-muted)]">
                {t('cred.cadaPasoDiceAntes')}
                <strong>{t('cred.noSeCambiaNunca')}</strong>.
              </p>
            </div>

            <ol className="mt-4 space-y-3 text-sm text-[var(--color-ink-muted)]">
              {[
                {
                  sesion: t('cred.sesionPanel'),
                  contenido: (
                    <>
                      {t('cred.paso1Antes')}
                      <strong>{t('cred.paso1Boton')}</strong>
                      {t('cred.paso1Medio')}
                      <br />
                      {t('cred.paso1Segunda')}
                      <strong>{t('cred.nuncaRegistrado')}</strong>
                      {t('cred.paso1Fin')}
                    </>
                  ),
                },
                {
                  sesion: t('cred.sesionPrincipal'),
                  contenido: (
                    <>
                      {t('cred.paso2Antes')}
                      <a
                        href="https://www.fanvue.com/settings/connect-management"
                        target="_blank"
                        rel="noreferrer noopener"
                        className="underline underline-offset-2 hover:text-[var(--color-accent)]"
                      >
                        Connect Management ↗
                      </a>{' '}
                      {t('cred.paso2Medio')}
                      <strong>{t('cred.registrarseManager')}</strong>
                      {t('cred.paso2Fin')}
                      <br />
                      {t('cred.paso2SiTeLleva')}
                      <strong>{t('cred.iniciaSesion')}</strong>
                      {t('cred.paso2Devuelve')}
                      <strong>{t('cred.noCreesCuenta')}</strong>
                      {t('cred.paso2Fin2')}
                    </>
                  ),
                },
                {
                  sesion: t('cred.sesionManager'),
                  contenido: <>{t('cred.paso3')}</>,
                },
                {
                  sesion: t('cred.sesionInfluencer'),
                  contenido: (
                    <>
                      {t('cred.paso4Antes')}
                      <strong>{t('cred.deElla')}</strong>
                      {t('cred.paso4Medio')}
                      <strong>{t('cred.correoDelManagerFuerte')}</strong>
                      {t('cred.paso4Fin')}
                      <br />
                      {t('cred.paso4Segunda')}
                    </>
                  ),
                },
                {
                  sesion: t('cred.sesionManager'),
                  contenido: <>{t('cred.paso5')}</>,
                },
                {
                  sesion: t('cred.sesionManager'),
                  contenido: (
                    <>
                      {t('cred.paso6Antes')}
                      <strong>{t('cred.conectarFanvue')}</strong>
                      {t('cred.paso6En')}
                      <strong>{t('cred.paso6Seccion')}</strong>
                      {t('cred.paso6Medio')}
                      <strong>{t('cred.delManager')}</strong>
                      {t('cred.paso6Fin')}
                      <br />
                      {/*
                        La ventana privada no es una precaución de más.

                        Fanvue reconoce la sesión que ya tenga el navegador, y para llegar hasta
                        acá hubo que entrar con la cuenta de la influencer en el paso anterior. Si
                        esa sesión sigue abierta, la autorización sale a nombre de ella: queda
                        conectada la cuenta equivocada, sin que nada avise.
                      */}
                      {t('cred.paso6Privada')}
                      <strong>{t('cred.ventanaPrivada')}</strong>
                      {t('cred.paso6PrivadaFin')}
                    </>
                  ),
                },
              ].map((paso, i) => (
                <li key={i} className="flex gap-3">
                  <span className="mt-0.5 shrink-0 font-mono text-xs">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="min-w-0">
                    <span className="mr-2 inline-block rounded-full bg-[var(--color-surface-sunken)] px-2 py-0.5 text-[0.65rem] font-medium">
                      {paso.sesion}
                    </span>
                    {paso.contenido}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/*
          El correo del manager, guardado.

          No es un secreto: es un dato que hay que copiar hasta quince veces, una por influencer,
          en una pantalla distinta cada vez. Escribir un alias largo a mano quince veces termina
          en una invitación mal dirigida que no avisa — la influencer simplemente no aparece
          entre las que el manager maneja, y descubrirlo lleva más que teclearlo bien.
        */}
        <div className="mt-4 flex flex-wrap items-end gap-2">
          <label className="flex-1">
            <span className="text-xs text-[var(--color-ink-muted)]">
              {t('cred.correoDelManager')}
            </span>
            <input
              type="email"
              value={email ?? estado.data.managerEmail ?? ''}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="manager@tudominio.com"
              className="mt-1 w-full rounded-lg border border-[var(--color-border-subtle)] bg-transparent px-3 py-1.5 text-sm"
            />
          </label>
          {/*
            Asignar la casilla propia, en vez de escribirla.

            Fanvue rechaza los alias con «+», así que el correo del manager tiene que ser una
            dirección real. Ésta vive en el dominio del panel y la resuelve el mismo manejador
            que las de las influencers — sin reglas de enrutamiento, que tienen tope de 200 por
            dominio y habría que crear a mano una por usuario.
          */}
          {estado.data.dominioPropio && !estado.data.managerEmail && (
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => {
                setBusy('casilla');
                void api
                  .post<{ managerEmail: string }>('/api/fanvue/cuenta', { asignarCasilla: true })
                  .then((r) => aviso.ok(t('cred.casillaAsignada', { direccion: r.managerEmail })))
                  .then(() => estado.reload())
                  .catch(() => undefined)
                  .finally(() => setBusy(null));
              }}
              className="rounded-lg border border-[var(--color-border-subtle)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)] disabled:opacity-40"
            >
              {t(busy === 'casilla' ? 'cred.asignando' : 'cred.asignarCasilla')}
            </button>
          )}

          <button
            type="button"
            disabled={busy !== null || email === null}
            onClick={() => void guardarEmail()}
            className="rounded-lg border border-[var(--color-border-subtle)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)] disabled:opacity-40"
          >
            {t(busy === 'email' ? 'comun.guardando' : 'comun.guardar')}
          </button>
          {estado.data.managerEmail && (
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(estado.data?.managerEmail ?? '');
                aviso.ok(t('cred.correoCopiado'));
              }}
              className="rounded-lg border border-[var(--color-border-subtle)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)]"
            >
              {t('comun.copiar')}
            </button>
          )}
        </div>

        {/*
          La contraseña del manager, guardada y cifrada.

          Es la que nadie escribe dos veces: se crea una vez, la cuenta se verifica con
          documento, y después se usa poco. Perderla no es perder un acceso — es perder la
          identidad verificada de la que cuelgan hasta quince perfiles, y recuperarla exige
          repetir la verificación. Sin un sitio en el panel terminaba en una nota o en un chat.

          El correo de arriba se muestra siempre porque hay que copiarlo quince veces. Ésta se
          esconde y hay que pedirla: se consulta dos veces al año, y dejarla a la vista la
          expone a cada persona que mire esta pantalla por encima del hombro.
        */}
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <label className="flex-1">
            <span className="text-xs text-[var(--color-ink-muted)]">
              {t('cred.passwordManager')}
              {estado.data.managerPasswordId ? t('cred.guardadaSufijo') : ''}
            </span>
            <input
              type="password"
              value={clave ?? ''}
              onChange={(e) => setClave(e.target.value)}
              placeholder={
                estado.data.managerPasswordId
                  ? t('cred.escribiNueva')
                  : t('cred.laDeTuCuenta')
              }
              autoComplete="off"
              className="mt-1 w-full rounded-lg border border-[var(--color-border-subtle)] bg-transparent px-3 py-1.5 text-sm"
            />
          </label>

          <button
            type="button"
            disabled={busy !== null || !clave}
            onClick={() => {
              setBusy('clave');
              void api
                .post('/api/fanvue/manager/password', { password: clave })
                .then(() => {
                  setClave(null);
                  aviso.ok(t('cred.passwordGuardada'));
                })
                .then(() => estado.reload())
                .catch(() => undefined)
                .finally(() => setBusy(null));
            }}
            className="rounded-lg border border-[var(--color-border-subtle)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)] disabled:opacity-40"
          >
            {t(busy === 'clave' ? 'comun.guardando' : 'comun.guardar')}
          </button>

          {estado.data.managerPasswordId && (
            /*
              El mismo «Ver» que las demás credenciales, y no uno propio.

              Pide la contraseña del panel —la sesión dice «esta pestaña ya entró», no «quien
              teclea ahora es el dueño»—, se vuelve a tapar sola a los treinta segundos y queda
              anotado en la auditoría. Escribir un botón aparte acá habría sido un camino sin
              esas tres cosas, que es como se pierden.
            */
            <VerSecreto credentialId={estado.data.managerPasswordId} />
          )}
        </div>

        <p className="mt-1.5 text-xs text-[var(--color-ink-muted)]">
          {t('cred.passwordQue')}
        </p>
      </div>

      {/*
        Lo que llegó a la casilla del manager.

        Va acá y no en una pantalla aparte porque el único motivo para mirar este correo es
        completar el alta que está justo arriba: la confirmación de Fanvue llega mientras se
        está en esta pantalla.
      */}
      {estado.data.correos.length > 0 && (
        <div className="mt-5 border-t border-[var(--color-border-subtle)] pt-4">
          <h3 className="text-base font-semibold">
            {t('cred.correoRecibido', { n: estado.data.correos.length })}
          </h3>
          <div className="mt-2">
            <ListaDeCorreos correos={estado.data.correos} onChange={() => estado.reload()} />
          </div>
        </div>
      )}

      <p className="mt-4 max-w-2xl text-xs text-[var(--color-ink-muted)]">
        {t('cred.quincePerfilesAntes')}
        <strong>{t('cred.quincePerfiles')}</strong>
        {t('cred.quincePerfilesDespues')}
        <a
          href="https://www.fanvue.com/"
          target="_blank"
          rel="noreferrer noopener"
          className="underline underline-offset-2 hover:text-[var(--color-accent)]"
        >
          {t('cred.abrirFanvue')}
        </a>
      </p>
    </>
  );

  if (embedded) {
    return (
      <section className="mt-5 border-t border-[var(--color-border-subtle)] pt-5">
        <h3 className="text-base font-semibold">2. Cuenta manager y perfiles</h3>
        <div className="mt-2">{content}</div>
      </section>
    );
  }

  return <Card title="Fanvue">{content}</Card>;
}

function AdminPassword({
  usingDefault,
  onChange,
}: {
  usingDefault: boolean;
  onChange: () => void;
}) {
  const { t } = useT();
  const aviso = useAviso();
  const [open, setOpen] = useState(usingDefault);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post('/api/auth/password', { currentPassword: current, newPassword: next });
      setCurrent('');
      setNext('');
      setDone(true);
      setOpen(false);
      onChange();
    } catch (caught) {
      // Lo que viene de la API ya salió como aviso; sólo queda lo que ni llegó a salir.
      if (!(caught instanceof ApiRequestError)) aviso.error(t('cred.noSePudoCambiar'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={t('cred.accesoAlPanel')} accent={usingDefault}>
      {usingDefault ? (
        <div className="rounded-xl border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/10 p-3">
          <p className="text-sm font-semibold text-[var(--color-danger)]">
            {t('cred.passwordDeFabrica')}
          </p>
          <p className="mt-1 text-sm">
            {t('cred.passwordDeFabricaQue')}
          </p>
        </div>
      ) : (
        <p className="text-sm text-[var(--color-ink-muted)]">
          {done
            ? t('cred.passwordCambiada')
            : t('cred.esLoUnico')}
        </p>
      )}

      {open ? (
        <form onSubmit={(event) => void submit(event)} className="mt-4 max-w-md space-y-3">
          <div>
            <label htmlFor="pass-actual" className="block text-sm font-medium">
              {t('cred.passwordActual')}
            </label>
            <input
              id="pass-actual"
              type="password"
              value={current}
              onChange={(event) => setCurrent(event.target.value)}
              required
              autoComplete="current-password"
              className="mt-1.5 w-full px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label htmlFor="pass-nueva" className="block text-sm font-medium">
              {t('cred.passwordNueva')}
            </label>
            <div className="relative mt-1.5">
              <input
                id="pass-nueva"
                type={revealed ? 'text' : 'password'}
                value={next}
                onChange={(event) => setNext(event.target.value)}
                required
                minLength={12}
                autoComplete="new-password"
                className="w-full py-2 pr-10 pl-3 text-sm"
              />
              <button
                type="button"
                onClick={() => setRevealed((value) => !value)}
                aria-label={t(revealed ? 'clave.ocultar' : 'clave.mostrar')}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-[var(--color-ink-faint)] transition hover:text-[var(--color-ink)]"
              >
                <Icon name={revealed ? 'eye-off' : 'eye'} size={16} />
              </button>
            </div>
            {/* Doce y no ocho: esto no protege una cuenta de foro, protege credenciales de
                pago y accesos a cuentas reales. */}
            <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
              {t('cred.minimo12')}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={busy || current.length === 0 || next.length < 12}>
              {t(busy ? 'cred.cambiando' : 'cred.cambiarPassword')}
            </Button>
            {!usingDefault && (
              <Button variant="secondary" onClick={() => setOpen(false)}>
                {t('comun.cancelar')}
              </Button>
            )}
          </div>
        </form>
      ) : (
        <div className="mt-3">
          <Button variant="secondary" onClick={() => setOpen(true)}>
            {t('cred.cambiarLaPassword')}
          </Button>
        </div>
      )}
    </Card>
  );
}

type KindSpec = {
  kind: CredentialKind;
  title: string;
  /** Qué hace este servicio en el sistema, en una línea. */
  purpose: string;
  /** Página exacta del proveedor donde se saca la credencial. */
  consoleUrl: string;
  consoleLabel: string;
  /**
   * Una guia externa, para las credenciales cuya alta no termina en esta pantalla.
   *
   * ── Por que solo algunas la tienen ────────────────────────────────────────
   * Porque en casi todas, pegar el secreto ES el alta: los pasos de arriba alcanzan. Telegram
   * no — despues del token hay que crear un canal por influencer y darle permisos al bot, y eso
   * no cabe en una tarjeta ni pertenece a esta pantalla.
   *
   * Es opcional a proposito: una guia por credencial seria una guia que nadie mantiene.
   */
  helpUrl?: string;
  helpLabel?: string;
  /** Pasos concretos en el sitio externo. Se muestran al desplegar el formulario. */
  steps: string[];
  /**
   * Segundo dato no secreto, si el servicio lo necesita.
   *
   * Era `needsAccountId`, atado a Cloudflare. El dominio propio necesita otro campo con la
   * misma forma —un valor público junto al secreto— y duplicar el mecanismo por servicio
   * habría multiplicado el formulario.
   */
  metadataField?: { key: string; label: string; hint: string; placeholder: string };
  secretLabel: string;
  secretPlaceholder: string;
  /** `true` si se pueden tener varias y alternar entre ellas. */
  multiple: boolean;
  /** Permisos que hay que marcar, con lo que desbloquea cada uno. */
  permissions?: { name: string; level: string; use: string }[];
  /** Cómo arreglar un token existente sin crear otro. */
  editNote?: string;
  /**
   * Ruta de retorno del OAuth, si el proveedor exige declararla.
   *
   * Se guarda la ruta y no la URL entera porque la parte de adelante depende de dónde esté
   * corriendo esto. Escribirla a mano es la forma más común de romper el OAuth —Meta compara
   * el texto exacto y rechaza por una barra de más—, así que la pantalla la arma con el
   * origen real y la ofrece para copiar.
   */
  redirectPath?: string;
  /**
   * `true` cuando el valor no es un secreto y conviene verlo al escribirlo.
   *
   * La casilla principal es la dirección de quien administra: ocultarla no protege nada y
   * quita la única comprobación posible, que es leerla.
   */
  secretIsPublic?: boolean;
  /**
   * Se pliega cuando este otro tipo ya está resuelto y éste todavía no.
   *
   * Sirve para alternativas: dos bloques que resuelven lo mismo y de los que alcanza con
   * uno. El correo tiene dos caminos —casilla principal con «+», o dominio propio— y
   * elegido uno, el otro deja de ser algo que falta. Mostrarlos los dos abiertos, en una
   * pantalla donde todo lo demás sí hay que completar, los hace leer como trabajo pendiente.
   *
   * Se declara en los dos sentidos y se resuelve solo: cada uno se pliega cuando el otro
   * está y él no. Con los dos cargados no se pliega ninguno, porque entonces sí hay algo
   * que revisar en ambos.
   *
   * Plegado, no oculto: quien lo quiera lo encuentra, y quien no, ve la lista de lo que
   * realmente falta.
   */
  foldWhen?: { kind: CredentialKind; note: string };
};

/**
 * El catálogo vive en la interfaz y no en el servidor a propósito: son instrucciones para
 * una persona, no datos del sistema. Cambiarlas no debería exigir un despliegue del Worker.
 */
const kindsDe = (t: (clave: Clave) => string): KindSpec[] => [
  {
    kind: 'cloudflare_account',
    title: t('kind.cf.title'),
    purpose: t('kind.cf.purpose'),
    consoleUrl: 'https://dash.cloudflare.com/profile/api-tokens',
    consoleLabel: t('kind.cf.console'),
    steps: [
      t('kind.cf.p1'),
      t('kind.cf.p2'),
      t('kind.cf.p3'),
      t('kind.cf.p4'),
      t('kind.cf.p5'),
    ],
    /**
     * Qué desbloquea cada permiso.
     *
     * La lista genérica «pedí estos seis» no dice cuál falta cuando algo no anda. Con el uso
     * al lado, la comprobación de arriba se vuelve legible: si falla «Workers Scripts» sabés
     * que podés generar igual y que sólo perdés el despliegue.
     */
    permissions: [
      {
        name: 'Workers AI',
        level: 'Edit',
        use: t('kind.cf.permAi'),
      },
      { name: 'Workers Scripts', level: 'Edit', use: t('kind.cf.permWorkers') },
      { name: 'D1', level: 'Edit', use: t('kind.cf.permD1') },
      {
        name: 'Workers KV Storage',
        level: 'Edit',
        use: t('kind.cf.permR2'),
      },
    ],
    editNote: t('kind.cf.edit'),
    metadataField: {
      key: 'accountId',
      label: t('kind.cf.metaLabel'),
      hint: t('kind.cf.metaHint'),
      placeholder: '00000000000000000000000000000000',
    },
    secretLabel: t('kind.cf.secret'),
    secretPlaceholder: 'cfut_…',
    multiple: true,
  },
  {
    kind: 'email_domain',
    title: t('kind.dominio.title'),
    purpose: t('kind.dominio.purpose'),
    consoleUrl: 'https://dash.cloudflare.com/',
    consoleLabel: t('kind.dominio.console'),
    steps: [
      t('kind.dominio.p1'),
      t('kind.dominio.p2'),
      t('kind.dominio.p3'),
      t('kind.dominio.p4'),
    ],
    metadataField: {
      key: 'domain',
      label: 'Dominio',
      hint: t('kind.dominio.metaHint'),
      placeholder: 'midominio.com',
    },
    secretLabel: t('kind.dominio.secret'),
    secretPlaceholder: 'tucorreo@gmail.com',
    multiple: false,
    secretIsPublic: true,
    foldWhen: {
      kind: 'email_base',
      note: t('kind.dominio.fold'),
    },
    editNote: t('kind.dominio.edit'),
  },
  {
    kind: 'email_base',
    title: 'Casilla principal',
    purpose: t('kind.casilla.purpose'),
    consoleUrl: 'https://support.google.com/a/users/answer/9282734',
    consoleLabel: t('kind.casilla.console'),
    steps: [
      t('kind.casilla.p1'),
      t('kind.casilla.p2'),
      t('kind.casilla.p3'),
    ],
    secretLabel: t('kind.casilla.secret'),
    secretPlaceholder: 'tucorreo@gmail.com',
    multiple: false,
    secretIsPublic: true,
    foldWhen: {
      kind: 'email_domain',
      note: t('kind.casilla.fold'),
    },
    editNote: t('kind.casilla.edit'),
  },
  {
    kind: 'fanvue_app',
    title: 'Fanvue',
    purpose: t('kind.fanvue.purpose'),
    consoleUrl: 'https://www.fanvue.com/api-keys',
    consoleLabel: t('kind.fanvue.console'),
    steps: [
      t('kind.fanvue.p1'),
      t('kind.fanvue.p2'),
      t('kind.fanvue.p3'),
      t('kind.fanvue.p4'),
      t('kind.fanvue.p5'),
      t('kind.fanvue.p6'),
      t('kind.fanvue.p7'),
    ],
    metadataField: {
      key: 'appId',
      label: 'Client ID',
      hint: t('kind.fanvue.metaHint'),
      placeholder: 'b0bb71e4-77e8-4991-af9e-8b2fb880f60e',
    },
    secretLabel: 'Client Secret',
    secretPlaceholder: t('kind.fanvue.secretPh'),
    multiple: false,
    redirectPath: '/api/oauth/fanvue/agencia/callback',
  },
  {
    kind: 'facebook_app',
    title: t('kind.fb.title'),
    purpose: t('kind.fb.purpose'),
    consoleUrl: 'https://developers.facebook.com/apps',
    consoleLabel: t('kind.meta.console'),
    steps: [
      t('kind.fb.p1'),
      t('kind.fb.p2'),
      t('kind.fb.p3'),
      t('kind.fb.p4'),
      t('kind.fb.p5'),
      t('kind.fb.p6'),
      t('kind.fb.p7'),
    ],
    metadataField: {
      key: 'appId',
      label: t('kind.fb.metaLabel'),
      hint: t('kind.fb.metaHint'),
      placeholder: '1234567890123456',
    },
    secretLabel: t('kind.fb.secret'),
    secretPlaceholder: t('kind.fb.secretPh'),
    multiple: false,
    redirectPath: '/api/oauth/facebook/callback',
  },
  {
    kind: 'instagram_app',
    title: t('kind.ig.title'),
    purpose: t('kind.ig.purpose'),
    consoleUrl: 'https://developers.facebook.com/apps',
    consoleLabel: t('kind.meta.console'),
    steps: [
      t('kind.ig.p1'),
      t('kind.ig.p2'),
      t('kind.ig.p3'),
      t('kind.ig.p4'),
      t('kind.ig.p5'),
      t('kind.ig.p6'),
      t('kind.ig.p7'),
      t('kind.ig.p8'),
      t('kind.ig.p9'),
      t('kind.ig.p10'),
      t('kind.ig.p11'),
      t('kind.ig.p12'),
      t('kind.ig.p13'),
    ],
    metadataField: {
      key: 'appId',
      label: t('kind.ig.metaLabel'),
      hint: t('kind.ig.metaHint'),
      placeholder: '1234567890123456',
    },
    secretLabel: t('kind.ig.secret'),
    secretPlaceholder: t('kind.ig.secretPh'),
    multiple: false,
    /**
     * La URI que Meta tiene que aceptar, calculada desde el origen real.
     *
     * Escribirla a mano es la forma más común de romper el OAuth: Meta compara el texto
     * exacto y rechaza por una barra de más. Mostrarla ya armada, desde donde el panel está
     * corriendo de verdad, saca el error del camino.
     */
    redirectPath: '/api/oauth/instagram/callback',
    editNote: t('kind.ig.edit'),
  },
  {
    kind: 'runware',
    title: 'Runware',
    purpose: t('kind.runware.purpose'),
    consoleUrl: 'https://my.runware.ai/keys',
    consoleLabel: t('kind.runware.console'),
    steps: [
      t('kind.runware.p1'),
      t('kind.runware.p2'),
      t('kind.runware.p3'),
      t('kind.runware.p4'),
    ],
    secretLabel: t('kind.runware.secret'),
    secretPlaceholder: '…',
    multiple: false,
  },
  {
    kind: 'telegram_bot',
    title: 'Telegram',
    purpose: t('kind.telegram.purpose'),
    consoleUrl: 'https://t.me/BotFather',
    consoleLabel: t('kind.telegram.console'),
    steps: [
      t('kind.telegram.p1'),
      t('kind.telegram.p2'),
      t('kind.telegram.p3'),
      t('kind.telegram.p4'),
    ],
    secretLabel: t('kind.telegram.secret'),
    secretPlaceholder: '123456789:AA…',
    helpUrl: 'https://claude.ai/code/artifact/1abf9a82-6e40-43a9-94bb-a22089bdd371',
    helpLabel: t('kind.telegram.guia'),
    /*
      Uno solo, no uno por influencer.

      Un bot puede ser administrador de todos los canales que haga falta, y el mensaje sale con la
      identidad del canal y no la del bot: quien lo lee no distingue uno de otro. Lo que cambia por
      influencer es a que canal escribe, y eso vive en la ficha de cada una.
    */
    multiple: false,
  },
  {
    kind: 'civitai',
    title: 'Civitai',
    purpose: t('kind.civitai.purpose'),
    consoleUrl: 'https://civitai.com/user/account',
    consoleLabel: t('kind.civitai.console'),
    steps: [
      t('kind.civitai.p1'),
      t('kind.civitai.p2'),
      t('kind.civitai.p3'),
      t('kind.civitai.p4'),
    ],
    secretLabel: t('kind.civitai.secret'),
    secretPlaceholder: '…',
    multiple: false,
  },
];

/**
 * El precio en Stars, al pie de la tarjeta de Telegram.
 *
 * ── Por que aca y no en Consumo, donde estaba ────────────────────────────────
 * Porque Consumo es lo que el sistema GASTA —neuronas, presupuesto, frenos— y esto es lo
 * contrario: lo que a vos te pagan. Ponerlo ahi era un error de categoria, y ademas dejaba la
 * configuracion de Telegram partida en dos pantallas.
 *
 * Aca queda todo lo de Telegram junto: el token, la guia y el precio. Hay precedente en esta
 * misma pantalla —`email_base` no es un secreto y vive aca porque es donde alguien lo va a
 * buscar—: Credenciales es la configuracion de lo externo, no solo la caja de secretos.
 *
 * ── Por que el dolar es texto y no un campo ──────────────────────────────────
 * Porque no se calcula nada con el. Telegram no publica tipo de cambio por API, asi que un
 * campo editable invitaria a creer que el sistema lo usa. Es una nota con fecha.
 */
function PrecioEnStars() {
  const { t } = useT();
  const state = useApi<{
    settings: {
      telegramStars: { imagenNsfw: number; imagenPremium: number; set: number; video: number };
      telegramStarsReferencia: { usd: number; anotadoEl: string };
    };
  }>('/api/settings');
  const [guardando, setGuardando] = useState(false);

  if (!state.data) return null;
  const { telegramStars, telegramStarsReferencia } = state.data.settings;

  return (
    <div className="mt-4 border-t border-[var(--color-border-subtle)] pt-4">
      <h3 className="text-base font-semibold">{t('stars.titulo')}</h3>
      <p className="mt-1 max-w-2xl text-sm text-[var(--color-ink-muted)]">{t('stars.bajada')}</p>

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-xs font-medium">{t('stars.imagenNsfw')}</span>
          <input
            type="number"
            min={1}
            max={25000}
            disabled={guardando}
            defaultValue={telegramStars.imagenNsfw}
            onBlur={(event) => {
              const valor = Math.trunc(Number(event.target.value));
              if (!Number.isFinite(valor) || valor < 1 || valor > 25000) return;
              if (valor === telegramStars.imagenNsfw) return;
              setGuardando(true);
              void api
                .patch('/api/settings', {
                  telegramStars: { ...telegramStars, imagenNsfw: valor },
                })
                .then(() => state.reload())
                .finally(() => setGuardando(false));
            }}
            className="mt-1 w-28 rounded-lg border border-[var(--color-border-subtle)] bg-transparent px-3 py-2 text-sm tabular-nums"
          />
        </label>
        <p className="pb-2 text-xs text-[var(--color-ink-muted)]">
          {t('stars.referencia', {
            usd: telegramStarsReferencia.usd.toFixed(2),
            fecha: telegramStarsReferencia.anotadoEl,
          })}
        </p>
      </div>

      <p className="mt-2 text-xs text-[var(--color-ink-faint)]">{t('stars.losOtros')}</p>
    </div>
  );
}

/**
 * El aviso por el que llegan las ventas: si está puesto, y si Telegram lo está usando.
 *
 * ── Por qué es un botón y no algo que se hace solo al guardar el token ────────
 * Porque `setWebhook` es exclusivo: registrar el nuestro DESCONECTA cualquier otro que ese bot
 * tuviera. Si el mismo bot lo usa otra cosa, esto se la lleva puesta, y eso lo decide una
 * persona — no un efecto secundario de pegar un token.
 *
 * ── Por qué se muestran los pendientes y el último error ──────────────────────
 * Porque son la diferencia entre «Telegram no avisa» y «avisa y no contestamos», que se arreglan
 * en lados opuestos. Sin ellos, un canal que cobra y no registra nada no tiene por dónde
 * empezarse a mirar.
 */
function AvisoDeVentas() {
  const { t } = useT();
  const aviso = useAviso();
  const state = useApi<{
    configurado: boolean;
    urlEsperada: string | null;
    info: {
      url: string | null;
      escuchaCompras: boolean;
      pendientes: number;
      ultimoError: string | null;
    } | null;
    sinAtribuir: number;
  }>('/api/integrations/telegram/webhook');
  const [guardando, setGuardando] = useState(false);

  if (!state.data?.configurado) return null;
  const { info, urlEsperada, sinAtribuir } = state.data;

  /*
    Que escuche compras Y que la dirección sea la nuestra.

    Las dos por separado no alcanzan: un webhook puesto contra otro dominio devuelve
    `escuchaCompras` en verdadero y las ventas se van a otra parte.
  */
  const listo = Boolean(info?.escuchaCompras && info.url && info.url === urlEsperada);

  return (
    <div className="mt-4 border-t border-[var(--color-border-subtle)] pt-4">
      <h3 className="text-base font-semibold">{t('ventas.avisoTitulo')}</h3>
      <p className="mt-1 max-w-2xl text-sm text-[var(--color-ink-muted)]">
        {t('ventas.avisoBajada')}
      </p>

      <p className="mt-2 text-sm">
        {listo ? (
          <span className="font-medium text-[var(--color-accent)]">{t('ventas.avisoListo')}</span>
        ) : (
          <span className="font-medium text-[var(--color-danger)]">
            {t('ventas.avisoFalta')}
          </span>
        )}
      </p>

      {info && (info.pendientes > 0 || info.ultimoError) && (
        <p className="mt-1 text-xs text-[var(--color-ink-muted)] break-words">
          {t('ventas.avisoDiagnostico', {
            pendientes: info.pendientes,
            error: info.ultimoError ?? '—',
          })}
        </p>
      )}

      {/*
        Las ventas que no se pudieron atribuir, que no aparecen en ninguna otra pantalla.

        Todo lo demás está agrupado por influencer y éstas no tienen: se guardan igual —el dinero
        entró— pero sin este número se guardarían para nadie. Que no sea cero significa que algo
        se rompió entre publicar y cobrar, y el log del webhook tiene el payload crudo.

        Sólo aparece cuando hay alguna: un cero permanente entrena a no leerlo.
      */}
      {sinAtribuir > 0 && (
        <p className="mt-1 text-xs font-medium text-[var(--color-warn)]">
          {t('ventas.sinAtribuir', { n: sinAtribuir })}
        </p>
      )}

      <button
        type="button"
        disabled={guardando}
        onClick={() => {
          setGuardando(true);
          void api
            .post('/api/integrations/telegram/webhook')
            .then(() => {
              aviso.ok(t('ventas.avisoRegistrado'));
              state.reload();
            })
            .catch(() => undefined)
            .finally(() => setGuardando(false));
        }}
        className="mt-3 rounded-lg border border-[var(--color-border-subtle)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)] disabled:opacity-40"
      >
        {t(guardando ? 'ventas.avisoRegistrando' : listo ? 'ventas.avisoRehacer' : 'ventas.avisoRegistrar')}
      </button>
    </div>
  );
}

export function Credentials() {
  const { t } = useT();
  const aviso = useAviso();
  const state = useApi<CredentialsData>('/api/credentials');
  const [busy, setBusy] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ id: string; check: CredentialCheck } | null>(null);

  if (state.loading && !state.data) return <Loading label={t('cred.cargando')} />;
  if (state.error) return <ErrorBanner error={state.error} onRetry={state.reload} />;
  if (!state.data) return null;

  const data = state.data;

  /**
   * Si ese tipo de credencial ya está cubierto, por el panel o por el entorno.
   *
   * Vale una guardada aunque no esté activa: alcanza para saber que la persona ya pasó por
   * ahí, que es lo único que decide si el bloque siguiente se muestra abierto o plegado.
   */
  const resolved = (kind: CredentialKind): boolean =>
    data.credentials.some((credential) => credential.kind === kind) ||
    Boolean(data.environmentFallback[kind]);

  const act = async (key: string, run: () => Promise<unknown>): Promise<void> => {
    setBusy(key);
    try {
      await run();
      state.reload();
    } catch (caught) {
      if (!(caught instanceof ApiRequestError)) aviso.error(t('cred.accionFallo'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">{t('cred.titulo')}</h1>
        <p className="mt-1.5 max-w-2xl text-sm text-[var(--color-ink-muted)]">
          {t('cred.bajadaAntes')}
          <em>{t('cred.generar')}</em>
          {t('cred.bajadaDespues')}
        </p>
      </div>

      {!data.encryptionAvailable && (
        <div
          role="alert"
          className="rounded-2xl border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/10 p-4"
        >
          <p className="text-sm font-semibold text-[var(--color-danger)]">
            {t('cred.faltaClaveCifrado')}
            <code className="font-mono">TOKEN_ENCRYPTION_KEY</code>
          </p>
          <p className="mt-1 text-sm">
            {t('cred.faltaClaveCifradoQue')}
            <code className="font-mono">make secrets</code>
            {t('cred.faltaClaveCifradoO')}
            <code className="font-mono">openssl rand -base64 32</code>.
          </p>
        </div>
      )}

      <AdminPassword usingDefault={data.usingDefaultPassword} onChange={state.reload} />

      {/*
        Los tokens de la extensión viven acá y no en Consumo, donde estaban.

        Un token de extensión ES una credencial: da acceso al panel desde otro programa. Que
        viviera en la pantalla del gasto era herencia de cuando esa pantalla fue acumulando
        cosas sin que el nombre las siguiera — y hacía que la propia extensión mandara a
        buscarlo a un sitio equivocado, porque nadie recuerda que un token está bajo «Consumo».
      */}
      <ExtensionTokenCard />

      {data.administraAppDeMeta === false && <CuentaDeFanvue />}

      {kindsDe(t).filter(
        /*
          La app de Meta es una sola para todo el panel y la configura quien administra.

          A los demás no se les muestra, y no por prudencia: su fila propia no existe, así que
          la tarjeta diría «Sin configurar» sobre algo que está puesto y funcionando. Publicar
          les anda igual porque el contenedor cae a la del administrador.

          El servidor también lo rechaza; esto solo sería cosmético.
        */
        (spec) =>
          data.administraAppDeMeta !== false ||
          (spec.kind !== 'instagram_app' &&
            spec.kind !== 'facebook_app' &&
            spec.kind !== 'fanvue_app'),
      ).map((spec) => {
        const section = (
          <KindSection
            key={spec.kind}
            spec={spec}
            credentials={data.credentials.filter((credential) => credential.kind === spec.kind)}
            envFallback={Boolean(data.environmentFallback[spec.kind])}
            emailBase={data.emailBase}
            busy={busy}
            flash={flash}
            onSave={(payload) =>
              act(`save-${spec.kind}`, async () => {
                const result = await api.post<{
                  credential: CredentialView;
                  check: CredentialCheck;
                }>('/api/credentials', { kind: spec.kind, ...payload });
                setFlash({ id: result.credential.id, check: result.check });
              })
            }
            onTest={(id) =>
              act(`test-${id}`, async () => {
                const result = await api.post<{ check: CredentialCheck }>(
                  `/api/credentials/${id}/test`,
                );
                setFlash({ id, check: result.check });
              })
            }
            onActivate={(id) => act(`on-${id}`, () => api.post(`/api/credentials/${id}/activate`))}
            onDeactivate={() =>
              act(`off-${spec.kind}`, () => api.post(`/api/credentials/${spec.kind}/deactivate`))
            }
            onDelete={(id) => act(`del-${id}`, () => api.del(`/api/credentials/${id}`))}
            footer={
              spec.kind === 'fanvue_app' ? (
                <CuentaDeFanvue embedded />
              ) : spec.kind === 'telegram_bot' ? (
                <>
                  <PrecioEnStars />
                  <AvisoDeVentas />
                </>
              ) : undefined
            }
          />
        );

        // Se pliega sólo si su alternativa está resuelta y éste no. Con los dos cargados
        // ninguno se pliega: entonces sí hay algo que revisar en ambos.
        const folded =
          spec.foldWhen !== undefined && resolved(spec.foldWhen.kind) && !resolved(spec.kind);
        if (!folded) return section;

        return (
          <details
            key={spec.kind}
            className="group rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)]"
          >
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-2xl p-4 text-sm">
              <span>
                <span className="font-semibold">{spec.title}</span>
                <span className="ml-2 text-[var(--color-ink-muted)]">{spec.foldWhen?.note}</span>
              </span>
              <span
                aria-hidden="true"
                className="shrink-0 text-[var(--color-ink-muted)] transition-transform group-open:rotate-90"
              >
                ›
              </span>
            </summary>
            <div className="p-4 pt-0">{section}</div>
          </details>
        );
      })}
    </div>
  );
}

function KindSection({
  spec,
  credentials,
  envFallback,
  emailBase,
  busy,
  flash,
  onSave,
  onTest,
  onActivate,
  onDeactivate,
  onDelete,
  footer,
}: {
  spec: KindSpec;
  credentials: CredentialView[];
  envFallback: boolean;
  emailBase?: string | null;
  busy: string | null;
  flash: { id: string; check: CredentialCheck } | null;
  onSave: (payload: {
    label: string;
    name: string;
    secret: string;
    accountId?: string;
  }) => Promise<void>;
  onTest: (id: string) => Promise<void>;
  onActivate: (id: string) => Promise<void>;
  onDeactivate: () => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  footer?: ReactNode;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(credentials.length === 0);
  const [label, setLabel] = useState('');
  const [name, setName] = useState('');
  const [secret, setSecret] = useState('');
  // Arranca oculto: mostrarlo es una acción explícita, no el estado por defecto.
  const [revealed, setRevealed] = useState(false);
  const [accountId, setAccountId] = useState('');

  const disabled = busy !== null;
  const active = credentials.find((credential) => credential.isActive);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    await onSave({
      label: label.trim() || (spec.multiple ? '' : spec.kind),
      name: name.trim() || label.trim(),
      secret,
      ...(spec.metadataField ? { [spec.metadataField.key]: accountId.trim() } : {}),
    });
    setSecret('');
    setRevealed(false);
  };

  return (
    <Card title={spec.title} accent={credentials.length === 0 && !envFallback}>
      {spec.kind === 'fanvue_app' && (
        <h3 className="mb-2 text-base font-semibold">{t('cred.appYPermisos')}</h3>
      )}
      <p className="max-w-2xl text-sm text-[var(--color-ink-muted)]">{spec.purpose}</p>

      {credentials.length === 0 && envFallback && (
        <p className="mt-3 rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-3 text-sm">
          {t('cred.porEntorno')}
        </p>
      )}

      {credentials.length > 0 && (
        <ul className="mt-4 space-y-3">
          {credentials.map((credential) => (
            <li key={credential.id}>
              <CredentialRow
                credential={credential}
                spec={spec}
                busy={busy}
                emailBase={emailBase}
                check={flash?.id === credential.id ? flash.check : credential.lastCheck}
                onTest={() => onTest(credential.id)}
                onActivate={() => onActivate(credential.id)}
                onDeactivate={onDeactivate}
                onDelete={() => onDelete(credential.id)}
              />
            </li>
          ))}
        </ul>
      )}

      {credentials.length === 0 && !envFallback && (
        <div className="mt-4">
          <EmptyState
            title={t('cred.sinConfigurar')}
            description={t('cred.sinConfigurarQue', { que: spec.title })}
          />
        </div>
      )}

      {/*
        El formulario está SIEMPRE, y eso es el arreglo.

        Se mostraba sólo con `spec.multiple || credentials.length === 0`, así que una credencial
        de las que van de a una no se podía reemplazar: había que borrarla primero para que
        apareciera el campo, y nada lo decía. Se descubrió con nueve credenciales que habían
        quedado ilegibles al cambiar la clave de cifrado — todas presentes, todas inservibles, y
        ninguna forma visible de volver a cargarlas.

        El backend ya hacía lo correcto: `upsert` reemplaza por (tipo, etiqueta) en vez de
        duplicar. Era la pantalla la que no dejaba llegar hasta él.
      */}
      <div className="mt-4 border-t border-[var(--color-border-subtle)] pt-4">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="flex items-center gap-2 text-sm font-medium text-[var(--color-accent)]"
        >
          <Icon name="plus" size={16} />
          {credentials.length === 0
            ? t('cred.configurar')
            : spec.multiple
              ? t('cred.agregarOtra')
              : /* Decirlo: al guardar, la que hay se pisa. */ t(
                  active ? 'cred.reemplazarEnUso' : 'cred.reemplazarLa',
                )}
        </button>

        {open && (
          <div className="mt-4 grid gap-5 lg:grid-cols-2">
            <div className="rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-4">
              <p className="text-sm font-medium">{t('cred.comoObtenerla')}</p>
              <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-sm text-[var(--color-ink-muted)]">
                {spec.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
              {spec.redirectPath && (
                <div className="mt-3 rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface)] p-3">
                  <p className="text-xs font-medium">{t('cred.uriRedireccion')}</p>
                  <div className="mt-1 flex items-center gap-2">
                    <code className="min-w-0 flex-1 truncate text-xs">
                      {window.location.origin + spec.redirectPath}
                    </code>
                    <CopyButton
                      text={window.location.origin + spec.redirectPath}
                      label={t('comun.copiar')}
                    />
                  </div>
                </div>
              )}
              {spec.permissions && (
                <table className="mt-3 w-full text-xs">
                  <thead>
                    <tr className="text-left text-[var(--color-ink-faint)]">
                      <th className="pb-1 pr-2 font-medium">{t('cred.permiso')}</th>
                      <th className="pb-1 font-medium">{t('cred.paraQue')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {spec.permissions.map((permission) => (
                      <tr key={permission.name} className="align-top">
                        <td className="py-0.5 pr-2 whitespace-nowrap">
                          <span className="font-medium">{permission.name}</span>{' '}
                          <span className="text-[var(--color-ink-faint)]">{permission.level}</span>
                        </td>
                        <td className="py-0.5 text-[var(--color-ink-muted)]">{permission.use}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {spec.editNote && (
                <p className="mt-3 rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface-raised)] p-2.5 text-xs text-[var(--color-ink-muted)]">
                  {spec.editNote}
                </p>
              )}

              <a
                href={spec.consoleUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="mt-3 inline-flex items-center gap-2 rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-raised)] px-3 py-2 text-sm font-medium transition hover:border-[var(--color-border-strong)] hover:bg-[var(--color-accent-soft)] [@media(pointer:coarse)]:min-h-11"
              >
                {spec.consoleLabel}
                <span aria-hidden="true">↗</span>
              </a>

              {/*
                La guia, cuando la credencial tiene una.

                Va al lado del enlace a la consola y no dentro de los pasos: los pasos son lo que
                se hace ACA, y esto es lo que queda por hacer despues, en otra parte.
              */}
              {spec.helpUrl && (
                <a
                  href={spec.helpUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="mt-3 ml-2 inline-flex items-center gap-2 rounded-xl border border-[var(--color-border-subtle)] px-3 py-2 text-sm font-medium transition hover:border-[var(--color-border-strong)] hover:bg-[var(--color-accent-soft)] [@media(pointer:coarse)]:min-h-11"
                >
                  {spec.helpLabel}
                  <span aria-hidden="true">↗</span>
                </a>
              )}
            </div>

            <form onSubmit={(event) => void submit(event)} className="space-y-3">
              {spec.multiple && (
                <Field
                  id={`${spec.kind}-label`}
                  label={t('cred.etiqueta')}
                  hint={t('cred.etiquetaQue')}
                >
                  <input
                    id={`${spec.kind}-label`}
                    value={label}
                    onChange={(event) => setLabel(event.target.value)}
                    placeholder={t('cred.etiquetaEjemplo')}
                    required
                    className="w-full px-3 py-2 text-sm"
                  />
                </Field>
              )}

              {spec.multiple && (
                <Field
                  id={`${spec.kind}-name`}
                  label={t('cred.nombre')}
                  hint={t('cred.nombreQue')}
                >
                  <input
                    id={`${spec.kind}-name`}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder={t('cred.nombreEjemplo')}
                    className="w-full px-3 py-2 text-sm"
                  />
                </Field>
              )}

              {spec.metadataField && (
                <Field
                  id={`${spec.kind}-meta`}
                  label={spec.metadataField.label}
                  hint={spec.metadataField.hint}
                >
                  <input
                    id={`${spec.kind}-meta`}
                    value={accountId}
                    onChange={(event) => setAccountId(event.target.value)}
                    placeholder={spec.metadataField.placeholder}
                    required
                    className="w-full px-3 py-2 font-mono text-sm"
                  />
                </Field>
              )}

              {/*
                  Un token se oculta mientras se escribe; una dirección de correo, no.
                  Enmascararla impedía lo único que uno quiere hacer ahí: comprobar que la
                  escribió bien. Y no es un secreto — es la casilla propia de quien administra.
                */}
              <Field
                id={`${spec.kind}-secret`}
                label={spec.secretLabel}
                hint={
                  spec.secretIsPublic
                    ? t('cred.secretoPublico')
                    : t('cred.secretoPrivado')
                }
              >
                <div className="relative">
                  <input
                    id={`${spec.kind}-secret`}
                    type={spec.secretIsPublic || revealed ? 'text' : 'password'}
                    inputMode={spec.secretIsPublic ? 'email' : 'text'}
                    value={secret}
                    onChange={(event) => setSecret(event.target.value)}
                    placeholder={spec.secretPlaceholder}
                    required
                    autoComplete={spec.secretIsPublic ? 'email' : 'off'}
                    className={
                      'w-full py-2 pl-3 font-mono text-sm ' +
                      (spec.secretIsPublic ? 'pr-3' : 'pr-10')
                    }
                  />
                  {/*
                      Ver lo que uno pega es la única forma de detectar un espacio de más o
                      una línea cortada. Arranca oculto —el valor sigue siendo un secreto—
                      pero mostrarlo es decisión de quien está sentado ahí.
                    */}
                  {!spec.secretIsPublic && (
                    <button
                      type="button"
                      onClick={() => setRevealed((value) => !value)}
                      aria-label={t(revealed ? 'clave.ocultar' : 'clave.mostrar')}
                      aria-pressed={revealed}
                      title={t(revealed ? 'clave.ocultar' : 'clave.mostrar')}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-[var(--color-ink-faint)] transition hover:text-[var(--color-ink)]"
                    >
                      <Icon name={revealed ? 'eye-off' : 'eye'} size={16} />
                    </button>
                  )}
                </div>
              </Field>

              <Button type="submit" disabled={disabled || secret.length === 0}>
                {t(
                  busy === `save-${spec.kind}` ? 'cred.comprobando' : 'cred.guardarYComprobar',
                )}
              </Button>

              <p className="text-xs text-[var(--color-ink-muted)]">
                {t('cred.alGuardarSeComprueba')}
              </p>
            </form>
          </div>
        )}
      </div>

      {active && spec.kind === 'cloudflare_account' && (
        <p className="mt-4 text-xs text-[var(--color-ink-muted)]">
          {t('cred.imagenesConAntes')}
          <strong>{active.name}</strong>
          {t('cred.imagenesConDespues')}
          <em>{t('cred.consumo')}</em>
          {t('cred.imagenesConFin')}
        </p>
      )}

      {footer}
    </Card>
  );
}

function CredentialRow({
  credential,
  spec,
  busy,
  check,
  emailBase,
  onTest,
  onActivate,
  onDeactivate,
  onDelete,
}: {
  credential: CredentialView;
  spec: KindSpec;
  busy: string | null;
  check: CredentialCheck | null;
  /** La dirección en claro, sólo para `email_base`. */
  emailBase?: string | null;
  onTest: () => Promise<void>;
  onActivate: () => Promise<void>;
  onDeactivate: () => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const { t } = useT();
  const disabled = busy !== null;

  return (
    <div
      className={
        'rounded-xl border p-4 ' +
        (credential.isActive
          ? 'border-[var(--color-accent)]/45 bg-[var(--color-accent-soft)]'
          : 'border-[var(--color-border-subtle)]')
      }
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{credential.name}</span>
            {credential.isActive ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-accent)]/40 bg-[var(--color-accent)]/12 px-2.5 py-0.5 text-xs font-medium text-[var(--color-accent)]">
                <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current" />
                {t('cred.enUso')}
              </span>
            ) : (
              /*
                «Guardada» a secas se leía como «ya está», y no lo está: una credencial guardada
                y no activa no genera nada. Alguien corrigió su token, pulsó «Probar», vio todo
                en verde, y el sistema le siguió diciendo que no había configurado nada — porque
                estaba guardada, comprobada y sin usar, tres estados que la etiqueta juntaba en
                una sola palabra tranquilizadora.
              */
              <span className="rounded-full border border-[var(--color-warn)]/40 bg-[var(--color-warn)]/10 px-2.5 py-0.5 text-xs text-[var(--color-warn)]">
                {t('cred.guardadaSinUsar')}
              </span>
            )}
          </div>
          <p className="mt-1 font-mono text-xs text-[var(--color-ink-muted)]">
            {spec.multiple && <>{credential.label} · </>}
            {/*
              La casilla principal se muestra entera: no es un secreto —es tu propia
              dirección— y enmascarada no sirve para comprobar que pusiste la correcta.
            */}
            {spec.kind === 'email_base' && emailBase ? emailBase : credential.secretHint}
            {spec.metadataField && credential.metadata[spec.metadataField.key] && (
              <> · {credential.metadata[spec.metadataField.key]}</>
            )}
          </p>
          {credential.lastCheckAt && (
            <p className="mt-0.5 text-xs text-[var(--color-ink-faint)]">
              {t('cred.comprobada', { cuando: formatDateTime(credential.lastCheckAt) })}
            </p>
          )}

          {/*
            El estado que no se deducía de nada: comprobada, correcta, y aun así sin efecto.
            El verde de «Probar» y la inactividad convivían en la pantalla sin contradecirse.
          */}
          {!credential.isActive && credential.lastCheck?.ok && (
            <p className="mt-1 text-xs text-[var(--color-warn)]">
              {t('cred.funcionaSinUsarAntes')}
              <strong>{t('cred.usarEsta')}</strong>
              {t('cred.funcionaSinUsarDespues')}
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {/*
            La casilla principal no lleva botón: se muestra entera más arriba porque no es un
            secreto. Pedir la contraseña para ver una dirección de correo propia sería un
            trámite sin nada del otro lado.
          */}
          {spec.kind !== 'email_base' && <VerSecreto credentialId={credential.id} />}
          <Button variant="secondary" disabled={disabled} onClick={() => void onTest()}>
            {t(busy === `test-${credential.id}` ? 'cred.probando' : 'cred.probar')}
          </Button>
          {credential.isActive ? (
            <Button variant="secondary" disabled={disabled} onClick={() => void onDeactivate()}>
              {t('cred.dejarDeUsar')}
            </Button>
          ) : (
            <Button disabled={disabled} onClick={() => void onActivate()}>
              {t(busy === `on-${credential.id}` ? 'cred.activando' : 'cred.usarEsta')}
            </Button>
          )}
          <Button variant="danger" disabled={disabled} onClick={() => void onDelete()}>
            {t('cred.borrar')}
          </Button>
        </div>
      </div>

      {check && <CheckReport check={check} />}
    </div>
  );
}

/**
 * Ver una credencial en claro, con la contraseña por delante.
 *
 * ── Por qué se pide la contraseña teniendo la sesión abierta ────────────────────
 * Porque son dos preguntas distintas. La sesión responde «esta pestaña ya entró alguna vez»;
 * la contraseña responde «quien está tecleando ahora es el dueño». Para leer el saldo alcanza
 * la primera. Para llevarse una clave de API, no.
 *
 * ── Por qué se vuelve a tapar sola ──────────────────────────────────────────────
 * Un secreto que queda en pantalla hasta que alguien recuerde ocultarlo termina proyectado en
 * una reunión. Treinta segundos alcanzan para copiarlo y no para olvidarse de él.
 */
function VerSecreto({ credentialId }: { credentialId: string }) {
  const { t } = useT();
  const aviso = useAviso();
  const [pidiendo, setPidiendo] = useState(false);
  const [password, setPassword] = useState('');
  const [secreto, setSecreto] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // El temporizador se limpia al desmontar: sin esto, cerrar la tarjeta antes de los treinta
  // segundos dejaría corriendo un `setState` sobre algo que ya no está.
  useEffect(() => {
    if (secreto === null) return;
    const reloj = setTimeout(() => setSecreto(null), 30_000);
    return () => clearTimeout(reloj);
  }, [secreto]);

  const revelar = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    try {
      const { secret } = await api.post<{ secret: string }>(
        `/api/credentials/${credentialId}/reveal`,
        { password },
      );
      setSecreto(secret);
      setPidiendo(false);
      setPassword('');
    } catch (caught) {
      if (!(caught instanceof ApiRequestError)) aviso.error(t('cred.noSePudoMostrar'));
    } finally {
      setBusy(false);
    }
  };

  if (secreto !== null) {
    return (
      <div className="mt-3 rounded-xl border border-[var(--color-warn)]/40 bg-[var(--color-warn)]/10 p-3">
        <p className="text-xs text-[var(--color-ink-muted)]">
          {t('cred.seTapaSola')}
        </p>
        <div className="mt-2 flex items-center gap-2">
          <code className="min-w-0 flex-1 overflow-x-auto font-mono text-xs break-all">
            {secreto}
          </code>
          <CopyButton text={secreto} />
        </div>
      </div>
    );
  }

  if (!pidiendo) {
    return (
      <Button variant="secondary" onClick={() => setPidiendo(true)}>
        {t('cred.ver')}
      </Button>
    );
  }

  return (
    <form onSubmit={(event) => void revelar(event)} className="mt-3 flex flex-wrap gap-2">
      <input
        type="password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        placeholder={t('cred.tuPasswordDelPanel')}
        autoComplete="current-password"
        // El campo aparece por un clic deliberado, así que el foco va donde el usuario mira.
        autoFocus
        className="min-w-48 flex-1 px-3 py-2 text-sm"
      />
      <Button type="submit" disabled={busy || password.length === 0}>
        {t(busy ? 'cred.verificando' : 'cred.mostrar')}
      </Button>
      <Button variant="secondary" onClick={() => setPidiendo(false)}>
        {t('comun.cancelar')}
      </Button>
    </form>
  );
}

/**
 * Cómo se dibuja una capacidad: anda, falla, o **no se pudo saber**.
 *
 * El tercero no es un adorno. Una comprobación que no pudo hacerse tiene `ok: true` por
 * descarte —no encontró nada malo porque no llegó a mirar—, y pintarla de verde jura algo que
 * nadie verificó. El 8 de agosto de 2026 el saldo de fal se volvió ilegible al rotar la clave y
 * la pantalla quedó toda en verde mientras del otro lado la cuenta estaba bloqueada.
 *
 * `verified` falta en los chequeos guardados antes de que esto existiera; ausente significa que
 * la comprobación sí se hizo.
 */
function estadoDe(capability: CredentialCheck['capabilities'][number]) {
  if (!capability.ok) return { simbolo: '✗', color: 'text-[var(--color-danger)]' } as const;
  if (capability.verified === false)
    return { simbolo: '?', color: 'text-[var(--color-warn)]' } as const;
  return { simbolo: '✓', color: 'text-[var(--color-ok)]' } as const;
}

/** Resultado de la comprobación: una línea por capacidad, y qué hacer si algo falla. */
function CheckReport({ check }: { check: CredentialCheck }) {
  return (
    <div className="mt-3 border-t border-[var(--color-border-subtle)] pt-3">
      <ul className="space-y-1">
        {check.capabilities.map((capability) => {
          const estado = estadoDe(capability);
          return (
            <li key={capability.key} className="flex items-start gap-2 text-sm">
              <span aria-hidden="true" className={`mt-0.5 shrink-0 ${estado.color}`}>
                {estado.simbolo}
              </span>
              <span>
                {capability.label}
                {/*
                El detalle se muestra siempre, no sólo cuando algo falla.

                La condición era `!capability.ok`, y tenía sentido cuando el detalle era el
                motivo del fallo. Dejó de tenerlo cuando empezó a traer datos: el saldo de fal
                —«USD 4,50 disponibles»— quedaba calculado y escondido detrás de un tilde verde,
                que es exactamente la señal vacía que la comprobación vino a reemplazar.

                Un ✓ dice «anda». El detalle dice cuánto te queda, que es lo que uno mira antes
                de lanzar una tanda.
              */}
                {capability.detail && (
                  <span className="text-[var(--color-ink-muted)]"> — {capability.detail}</span>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {check.remedy && (
        <p className="mt-3 rounded-xl border border-[var(--color-warn)]/35 bg-[var(--color-warn)]/10 p-3 text-sm">
          {check.remedy}
        </p>
      )}
    </div>
  );
}

function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium">
        {label}
      </label>
      <div className="mt-1.5">{children}</div>
      <p className="mt-1 text-xs text-[var(--color-ink-muted)]">{hint}</p>
    </div>
  );
}

/**
 * El token con el que la extensión del navegador lee tus listas de captación.
 *
 * ── Por qué está acá y no en la ficha de una influencer ────────────────────────
 * Nació ahí, atado a una influencer, dando por hecho un perfil de Chrome por cada una. Ese no es
 * el flujo real: se trabaja en un solo navegador cambiando de cuenta con el selector de Instagram,
 * y un token por influencer obligaba a instalar la extensión tantas veces como influencers
 * hubiera. Ahora es uno solo y la extensión elige con cuál trabajar en un desplegable.
 *
 * ── Por qué el valor se muestra una sola vez ───────────────────────────────────
 * Se guarda el hash, no el token. Volver a mostrarlo exigiría guardarlo en claro, y entonces
 * quien leyera la base tendría las listas de todas tus influencers. Perderlo cuesta generar otro.
 */
function ExtensionTokenCard() {
  const { t } = useT();
  const aviso = useAviso();
  const lista = useApi<{
    tokens: {
      id: string;
      label: string | null;
      scope: string;
      createdAt: string;
      lastUsedAt: string | null;
    }[];
  }>('/api/extension-tokens');

  const [label, setLabel] = useState('');
  /** Para qué sirve el token que se está por crear. */
  const [scope, setScope] = useState('extension');
  /** El valor recién creado. Vive sólo en esta pantalla y se pierde al recargar, como debe. */
  const [nuevo, setNuevo] = useState<string | null>(null);
  const [creando, setCreando] = useState(false);
  const [regenerando, setRegenerando] = useState<string | null>(null);

  /*
    El token nuevo se muestra donde ya se muestra el recién creado.

    Es el mismo dato con la misma vida —visible una vez, y ya— así que reusar ese sitio evita
    dos cajas que dicen lo mismo y evita que una de las dos se olvide de advertirlo.
  */
  const regenerar = async (id: string): Promise<void> => {
    setRegenerando(id);
    try {
      const { token } = await api.post<{ token: string }>(
        `/api/extension-tokens/${id}/regenerar`,
        {},
      );
      setNuevo(token);
      aviso.ok(t('ext.tokenRegenerado'));
      lista.reload();
    } catch (caught) {
      if (!(caught instanceof ApiRequestError)) aviso.error(t('ext.noSePudoRegenerar'));
    } finally {
      setRegenerando(null);
    }
  };

  const crear = async (): Promise<void> => {
    setCreando(true);
    try {
      const resultado = await api.post<{ token: string }>('/api/extension-tokens', {
        label: label.trim(),
        scope,
      });
      setNuevo(resultado.token);
      setLabel('');
      await lista.reload();
    } catch {
      aviso.error(t('ext.noSePudoCrear'));
    } finally {
      setCreando(false);
    }
  };

  const tokens = lista.data?.tokens ?? [];

  return (
    <Card title={t('ext.titulo')}>
      <p className="text-sm text-[var(--color-ink-muted)]">
        {t('ext.queHaceAntes')}
        <strong>{t('ext.noSiguePorVos')}</strong>
        {t('ext.queHaceDespues')}
      </p>

      {/*
        La dirección del panel, junto al token y no en la cabeza de nadie.

        La extensión pide DOS cosas —dirección y token— y esta tarjeta sólo daba una. La otra hay
        que deducirla, y la primera que a uno se le ocurre es la `.workers.dev` que aparece al
        desplegar: funciona hoy y deja de funcionar el día que cambie el nombre del Worker, sin
        que nada relacione una cosa con la otra.

        Sale de `window.location.origin` y no de una constante: es, por definición, la dirección
        desde la que se está mirando esto. Una constante se queda vieja al cambiar de dominio, y
        se queda vieja en silencio.
      */}
      <div className="mt-4 rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-3">
        <p className="text-xs font-medium">{t('ext.direccionDelPanel')}</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <code className="min-w-0 flex-1 rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface-raised)] px-2 py-1 font-mono text-xs break-all">
            {window.location.origin}
          </code>
          <CopyButton text={window.location.origin} label={t('comun.copiar')} />
        </div>
        <p className="mt-2 text-xs text-[var(--color-ink-muted)]">
          {t('ext.direccionQueAntes')}
          <code className="font-mono">workers.dev</code>
          {t('ext.direccionQueDespues')}
        </p>
      </div>

      {/*
        El alcance, elegido al crear y no después.

        Un token que sirviera para las dos cosas haría que conceder una concediera la otra sin que
        nadie lo decida. Y no se puede cambiar después a propósito: cambiarle el alcance a un token
        que ya está pegado en algún lado es darle permisos a algo que no se está mirando.
      */}
      <label className="mt-4 block text-xs font-medium" htmlFor="alcance-token">
        {t('ext.paraQueSirve')}
      </label>
      <select
        id="alcance-token"
        value={scope}
        onChange={(event) => setScope(event.target.value)}
        className="mt-1 w-full rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-2 text-sm"
      >
        <option value="extension">{t('ext.opcionExtension')}</option>
        <option value="mcp_lectura">{t('ext.opcionMcp')}</option>
        <option value="mcp_generacion">{t('ext.opcionMcpGeneracion')}</option>
      </select>

      {/*
        El aviso del que GENERA es distinto y más fuerte, no el mismo con una línea más.

        Este alcance gasta dinero. Un texto que empiece contando lo que puede leer entierra eso al
        final, que es donde no se lee. Empieza por lo que cuesta.
      */}
      {scope === 'mcp_generacion' && (
        <p className="mt-2 rounded-xl border border-[var(--color-accent)] bg-[var(--color-accent-soft)] p-3 text-xs">
          {t('ext.mcpGenera')}
        </p>
      )}

      {scope === 'mcp_lectura' && (
        <p className="mt-2 rounded-xl bg-[var(--color-surface-sunken)] p-3 text-xs text-[var(--color-ink-muted)]">
          {t('ext.mcpQueAntes')}
          <strong>{t('ext.leer')}</strong>
          {t('ext.mcpQueMedio')}
          <code className="font-mono">/mcp</code>
          {t('ext.mcpQueFin')}
        </p>
      )}

      {nuevo && (
        <div className="mt-4 rounded-xl border border-[var(--color-accent)] bg-[var(--color-accent-soft)] p-3">
          <p className="text-xs font-medium">{t('ext.copialoAhora')}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 break-all font-mono text-xs">{nuevo}</code>
            <CopyButton text={nuevo} label={t('comun.copiar')} />
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <input
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          placeholder={t('ext.paraReconocerlo')}
          className="min-w-0 flex-1 rounded-xl border border-[var(--color-border-subtle)] px-3 py-2 text-sm"
        />
        <Button disabled={creando} onClick={() => void crear()}>
          {t(creando ? 'ext.creando' : 'ext.crearToken')}
        </Button>
      </div>

      {tokens.length > 0 && (
        <ul className="mt-3 divide-y divide-[var(--color-border-subtle)] text-sm">
          {tokens.map((token) => (
            <li key={token.id} className="flex flex-wrap items-center gap-3 py-2">
              <span className="min-w-0 flex-1 truncate">
                {token.label ?? (
                  <span className="text-[var(--color-ink-faint)]">{t('ext.sinNombre')}</span>
                )}
              </span>
              {/*
                Cuándo se usó por última vez, no cuándo se creó: es lo que distingue un token vivo
                de uno olvidado en un navegador que ya nadie abre, que es la pregunta que se hace
                quien está por revocar uno.
              */}
              {/* El alcance, al lado del nombre: es lo que decide qué puede hacer quien lo tenga. */}
              <span className="rounded-md bg-[var(--color-surface-sunken)] px-1.5 py-0.5 text-xs text-[var(--color-ink-muted)]">
                {t(
                  token.scope === 'mcp_generacion'
                    ? 'ext.mcpGeneracion'
                    : token.scope === 'mcp_lectura'
                      ? 'ext.mcpLectura'
                      : 'ext.extension',
                )}
              </span>
              <span className="text-xs text-[var(--color-ink-muted)]">
                {token.lastUsedAt
                  ? t('ext.usado', { cuando: haceCuanto(token.lastUsedAt) })
                  : t('ext.sinUsar')}
              </span>
              {/*
                Regenerar, y no «ver».

                Lo guardado es el hash: el valor no existe en ningún lado, y mostrarlo exigiría
                guardarlo recuperable — quien leyera la base tendría acceso a las listas de
                trabajo de todas las influencers.

                Y no hace falta: quien perdió su token no quiere verlo, quiere uno que funcione.
                Esto lo da y además deja inservible el viejo, que es lo correcto cuando no se
                sabe dónde quedó.
              */}
              <button
                type="button"
                onClick={() => void regenerar(token.id)}
                className="rounded-lg px-2 py-1 text-xs text-[var(--color-ink-muted)] transition hover:text-[var(--color-accent)]"
              >
                {t(regenerando === token.id ? 'ext.regenerando' : 'ext.regenerar')}
              </button>
              <button
                type="button"
                onClick={() =>
                  void api.del(`/api/extension-tokens/${token.id}`).then(() => lista.reload())
                }
                className="rounded-lg px-2 py-1 text-xs text-[var(--color-ink-muted)] transition hover:text-[var(--color-danger)]"
              >
                {t('ext.revocar')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
